import { sliderAnatomy as parts } from '@chakra-ui/anatomy'
import {
  createMultiStyleConfigHelpers,
  cssVar,
  defineStyle,
} from '@chakra-ui/styled-system'
import { orient, transparentize } from '@chakra-ui/theme-tools'

const { defineMultiStyleConfig, definePartsStyle } =
  createMultiStyleConfigHelpers(parts.keys)

const $thumbSize = cssVar('slider-thumb-size')
const $trackSize = cssVar('slider-track-size')
const $thumbBorderColor = cssVar('slider-thumb-border-color')
const $bg = cssVar('slider-bg')

const baseStyleContainer = defineStyle((props) => {
  const { orientation } = props

  return {
    display: 'inline-block',
    position: 'relative',
    cursor: 'pointer',
    _disabled: {
      opacity: 0.6,
      cursor: 'default',
      pointerEvents: 'none',
    },
    ...orient({
      orientation,
      vertical: { h: '100%' },
      horizontal: { w: '100%' },
    }),
  }
})

const baseStyleTrack = defineStyle((props) => {
  const orientationStyles = orient({
    orientation: props.orientation,
    horizontal: { h: $trackSize.reference },
    vertical: { w: $trackSize.reference },
  })

  return {
    ...orientationStyles,
    overflow: 'hidden',
    borderRadius: 'sm',
    [$bg.variable]: 'colors.neutral.100',
    _dark: {
      [$bg.variable]: 'colors.whiteAlpha.200',
    },
    _disabled: {
      [$bg.variable]: 'colors.gray.300',
      _dark: {
        [$bg.variable]: 'colors.whiteAlpha.300',
      },
    },
    bg: $bg.reference,
  }
})

const baseStyleThumb = defineStyle((props) => {
  const { orientation, colorScheme: c } = props
  const orientationStyle = orient({
    orientation,
    vertical: {
      left: '50%',
      transform: 'translateX(-50%)',
      _active: {
        transform: 'translateX(-50%) scale(1.15)',
      },
    },
    horizontal: {
      top: '50%',
      transform: 'translate(0, -50%)',
      _active: {
        transform: 'translate(0, -50%) scale(1.15)',
      },
    },
  })

  return {
    ...orientationStyle,
    [$thumbBorderColor.variable]: 'checkbox-border-checked',
    w: $thumbSize.reference,
    h: $thumbSize.reference,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    zIndex: 1,
    borderRadius: 'full',
    bg: `${c}.500`,
    boxShadow: 'none',
    border: '1px solid',
    borderColor: 'checkbox-border-checked',
    outline: '3px solid',
    outlineColor: transparentize(`${c}.500`, 0.25),
    transitionProperty: 'transform',
    transitionDuration: 'normal',
    _focusVisible: {
      boxShadow: 'none',
    },
    _disabled: {
      bg: 'gray.300',
      cursor: 'not-allowed',
      outlineColor: 'blackAlpha.300',
    },
  }
})

const baseStyleFilledTrack = defineStyle((props) => {
  const { colorScheme: c } = props

  return {
    width: 'inherit',
    height: 'inherit',
    [$bg.variable]: `colors.${c}.500`,
    _dark: {
      [$bg.variable]: `colors.${c}.200`,
    },
    bg: $bg.reference,
  }
})

const baseStyle = definePartsStyle((props) => ({
  container: baseStyleContainer(props),
  track: baseStyleTrack(props),
  thumb: baseStyleThumb(props),
  filledTrack: baseStyleFilledTrack(props),
}))

const sizeLg = definePartsStyle({
  container: {
    [$thumbSize.variable]: 'sizes.4',
    [$trackSize.variable]: 'sizes.1',
  },
})

const sizeMd = definePartsStyle({
  container: {
    [$thumbSize.variable]: 'sizes.3.5',
    [$trackSize.variable]: 'sizes.1',
  },
})

const sizeSm = definePartsStyle({
  container: {
    [$thumbSize.variable]: 'sizes.2.5',
    [$trackSize.variable]: 'sizes.0.5',
  },
})

const sizes = {
  lg: sizeLg,
  md: sizeMd,
  sm: sizeSm,
}

export const sliderTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
  defaultProps: {
    size: 'md',
    colorScheme: 'green',
  },
})
